/* eslint-disable no-console */
import { useCallback, useState } from 'react';
import { formatFileSize, IFileDataWithSharing, SharedUser } from '../utils/file-manager';
import { v4 as uuidv4 } from 'uuid';
import { downloadFileById } from '../../../services/storage-service';
import { useConfig } from 'hooks/use-config';
import { useRenameDocumentFile, useShareDocumentFile, useSoftDeleteDocumentFile, useUnshareDocumentFile, useUploadFileUnderDocument } from './use-document-manager';
import { ShareholderType } from '../services/document-manager-service';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';

interface UseFileManagerProps {
  onCreateFile?: () => void;
  defaultViewMode?: 'grid' | 'list';
}

export const useFileManager = ({ onCreateFile, defaultViewMode }: UseFileManagerProps = {}) => {
  const { configurationSettings } = useConfig();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(defaultViewMode || 'grid');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [newFiles, setNewFiles] = useState<IFileDataWithSharing[]>([]);
  const [newFolders, setNewFolders] = useState<IFileDataWithSharing[]>([]);
  const [renamedFiles, setRenamedFiles] = useState<Map<string, IFileDataWithSharing>>(new Map());
  const [fileSharedUsers, setFileSharedUsers] = useState<{ [key: string]: SharedUser[] }>({});
  const [filePermissions, setFilePermissions] = useState<{
    [key: string]: { [key: string]: string };
  }>({});

  // Modal states
  const [isSharedWithTenantModalOpen, setIsSharedWithTenantModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [fileToShare, setFileToShare] = useState<IFileDataWithSharing | null>(null);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState<IFileDataWithSharing | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedFileForDetails, setSelectedFileForDetails] = useState<IFileDataWithSharing | null>(
    null
  );
  const uploadFileUnderDocument = useUploadFileUnderDocument();
  const renameDocumentFile = useRenameDocumentFile();
  const { mutateAsync: shareDocumentFile } = useShareDocumentFile();
  const { mutateAsync: unshareDocumentFile } = useUnshareDocumentFile();
  const { mutateAsync: softDeleteDocumentFile } = useSoftDeleteDocumentFile();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const getFileTypeFromFile = (file: File): 'File' | 'Image' | 'Audio' | 'Video' => {
    const type = file.type;
    if (type.startsWith('image/')) return 'Image';
    if (type.startsWith('audio/')) return 'Audio';
    if (type.startsWith('video/')) return 'Video';
    return 'File';
  };

  const handleFileUpload = useCallback(async (files: File[], isSharedContext = false) => {
    for (const file of files) {
      try {
        const uploadedFile = await uploadFileUnderDocument.mutateAsync(file, {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('FILE_UPLOADED_SUCCESSFULLY'),
            });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: t('ERROR'),
              description: t('FILE_UPLOAD_FAILED'),
            });
          },
        });

        const fileData: IFileDataWithSharing = {
          id: uploadedFile.fileId || uuidv4(),
          name: uploadedFile.metadata.fileName || file.name,
          fileType: getFileTypeFromFile(file),
          size: formatFileSize(file.size),
          lastModified: new Date(uploadedFile.lastUpdatedDate || Date.now()),
          ...(isSharedContext && {
            sharedBy: { id: 'current', name: 'Current User' },
            sharedDate: new Date(),
          }),
        };
        setNewFiles((prev) => [...prev, fileData]);
        queryClient.invalidateQueries({ queryKey: ['document-files'] });
      } catch (error: any) {
        const status = error?.status;
        if (status == 400) {
          toast({
            variant: 'destructive',
            title: t('FILE_UPLOAD_FAILED'),
            description: t('UNSUPPORTED_FILE_DESC'),
          });
          return;
        }
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FILE_UPLOAD_FAILED'),
        });
      }
    }
  }, []);

  const handleFolderCreate = useCallback((folderName: string, isSharedContext = false) => {
    const newFolder: IFileDataWithSharing = {
      id: uuidv4(),
      name: folderName,
      fileType: 'Folder',
      size: '0',
      lastModified: new Date(),
      ...(isSharedContext && {
        sharedBy: { id: 'current', name: 'Current User' },
        sharedDate: new Date(),
      }),
    };

    setNewFolders((prev) => [...prev, newFolder]);
  }, []);

  const handleDownload = useCallback(async (file: IFileDataWithSharing) => {
    const isDownloaded = await downloadFileById(file.id, file.name, configurationSettings, undefined);
    if (!isDownloaded) {
      trackEvent(EVENTS.ERROR(new Error("Failed to download file"), {
        endpoint: 'protected-file-download',
        method: 'GET',
      }));
    }
  }, []);

  const handleDelete = useCallback(async (file: IFileDataWithSharing) => {
    try {
      await softDeleteDocumentFile({
        fileIds: [file.id]
      }, {
        onSuccess: () => {
          setNewFiles((prev) => prev.filter((f) => f.id !== file.id));
          setNewFolders((prev) => prev.filter((f) => f.id !== file.id));
          setRenamedFiles((prev) => {
            const newMap = new Map(prev);
            newMap.delete(file.id);
            return newMap;
          });
          setFileSharedUsers((prev) => {
            const newSharedUsers = { ...prev };
            delete newSharedUsers[file.id];
            return newSharedUsers;
          });
          setFilePermissions((prev) => {
            const newPermissions = { ...prev };
            delete newPermissions[file.id];
            return newPermissions;
          });
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('FILE_DELETED_SUCCESSFULLY'),
          });
          // queryClient.invalidateQueries({ queryKey: ['document-files'] });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: t('FILE_DELETED_FAILED'),
          });
        }
      })
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('FILE_DELETED_FAILED'),
      });
    }
  }, []);


  const handleShare = useCallback((file: IFileDataWithSharing) => {
    setFileToShare(file);
    setIsShareModalOpen(true);
  }, []);

  const handleShareModalClose = useCallback(() => {
    setIsShareModalOpen(false);
    setFileToShare(null);
  }, []);

  const handleShareWithTenant = useCallback((file: IFileDataWithSharing) => {
    setFileToShare(file);
    setIsSharedWithTenantModalOpen(true);
  }, []);

  const handleSharedWithTenantModalClose = useCallback(() => {
    setIsSharedWithTenantModalOpen(false);
    setFileToShare(null);
  }, []);

  const handleSharedWithTenantConfirm = useCallback(async () => {
    if (!fileToShare) return;

    if (fileToShare.isSharedWithTenant) {
      try {
        await unshareDocumentFile({ fileId: fileToShare.id, users: [{ id: configurationSettings.blocksKey, type: 'TENANT' as ShareholderType }] }, {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: t('SUCCESS'),
              description: t('FILE_UNSHARED_SUCCESSFULLY'),
            });
            queryClient.invalidateQueries({ queryKey: ['document-files'] });
          },
          onError: () => {
            toast({
              variant: 'destructive',
              title: t('ERROR'),
              description: t('FILE_UNSHARED_FAILED'),
            });
          },
          onSettled: () => {
            setIsSharedWithTenantModalOpen(false);
            setFileToShare(null);
          }
        });
      } catch (error) {
        console.error('Error unsharing file:', error);
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FILE_UNSHARED_FAILED'),
        });
      }
      return;
    }

    try {
      await shareDocumentFile({ fileId: fileToShare.id, userIds: [{ id: configurationSettings.blocksKey, type: 'TENANT' as ShareholderType }] }, {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: t('SUCCESS'),
            description: t('FILE_SHARED_SUCCESSFULLY'),
          });
          queryClient.invalidateQueries({ queryKey: ['document-files'] });
        },
        onError: () => {
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: t('FILE_SHARED_FAILED'),
          });
        },
        onSettled: () => {
          setIsSharedWithTenantModalOpen(false);
          setFileToShare(null);
        }
      });
    } catch (error) {
      console.error('Error sharing file:', error);
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('FILE_SHARED_FAILED'),
      });
    }
  }, [fileToShare]);

  const handleRename = useCallback((file: IFileDataWithSharing) => {
    setFileToRename(file);
    setIsRenameModalOpen(true);
  }, []);

  const handleRenameConfirm = useCallback(
    async (newName: string) => {
      if (!fileToRename) return;

      try {
        await renameDocumentFile.mutateAsync({ fileId: fileToRename.id, newName });
        const updatedFile = {
          ...fileToRename,
          name: newName,
          lastModified: new Date(),
        };

        const isLocalFile =
          fileToRename.fileType === 'Folder'
            ? newFolders.some((folder) => folder.id === fileToRename.id)
            : newFiles.some((file) => file.id === fileToRename.id);

        if (isLocalFile) {
          if (fileToRename.fileType === 'Folder') {
            setNewFolders((prev) =>
              prev.map((folder) => (folder.id === fileToRename.id ? updatedFile : folder))
            );
          } else {
            setNewFiles((prev) =>
              prev.map((file) => (file.id === fileToRename.id ? updatedFile : file))
            );
          }
        } else {
          setRenamedFiles((prev) => new Map(prev.set(fileToRename.id, updatedFile)));
        }
        toast({
          variant: 'success',
          title: t('SUCCESS'),
          description: t('FILE_RENAMED_SUCCESSFULLY'),
        });
        queryClient.invalidateQueries({ queryKey: ['document-files'] });
      } catch (error) {
        console.error('Error renaming file:', error);
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: t('FILE_RENAMED_FAILED'),
        });
      } finally {
        setIsRenameModalOpen(false);
        setFileToRename(null);
      }
    },
    [fileToRename, newFiles, newFolders]
  );

  const handleRenameModalClose = useCallback(() => {
    setIsRenameModalOpen(false);
    setFileToRename(null);
  }, []);

  const handleRenameUpdate = useCallback(
    (oldFile: IFileDataWithSharing, newFile: IFileDataWithSharing) => {
      const isLocalFile =
        oldFile.fileType === 'Folder'
          ? newFolders.some((folder) => folder.id === oldFile.id)
          : newFiles.some((file) => file.id === oldFile.id);

      if (isLocalFile) {
        if (oldFile.fileType === 'Folder') {
          setNewFolders((prev) =>
            prev.map((folder) => (folder.id === oldFile.id ? newFile : folder))
          );
        } else {
          setNewFiles((prev) => prev.map((file) => (file.id === oldFile.id ? newFile : file)));
        }
      } else {
        setRenamedFiles((prev) => new Map(prev.set(oldFile.id, newFile)));
      }
    },
    [newFiles, newFolders]
  );

  const handleViewModeChange = useCallback((mode: string) => {
    setViewMode(mode as 'grid' | 'list');
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreateFile = useCallback(() => {
    if (onCreateFile) {
      onCreateFile();
    }
  }, [onCreateFile]);

  const getUpdatedFile = useCallback(
    (file: IFileDataWithSharing): IFileDataWithSharing => {
      // const renamedFile = renamedFiles.get(file.id);
      // if (renamedFile) {
      //   return renamedFile;
      // }

      // const isLocalFile =
      //   file.fileType === 'Folder'
      //     ? newFolders.some((folder) => folder.id === file.id)
      //     : newFiles.some((f) => f.id === file.id);

      // if (isLocalFile) {
      //   const localFile =
      //     file.fileType === 'Folder'
      //       ? newFolders.find((folder) => folder.id === file.id)
      //       : newFiles.find((f) => f.id === file.id);
      //   return localFile || file;
      // }


      const sharedUsers = fileSharedUsers[file.id];
      const permissions = filePermissions[file.id];

      if (sharedUsers || permissions) {
        return {
          ...file,
          sharedWith: sharedUsers || file.sharedWith,
          sharePermissions: permissions || file.sharePermissions,
          isShared:
            (sharedUsers && sharedUsers.length > 0) ||
            (file.sharedWith && file.sharedWith.length > 0),
        };
      }

      return file;
    },
    [newFiles, newFolders, renamedFiles, fileSharedUsers, filePermissions]
  );

  const handleViewDetails = useCallback(
    (file: IFileDataWithSharing) => {
      const updatedFile = getUpdatedFile(file);
      setSelectedFileForDetails(updatedFile);
      setIsDetailsOpen(true);
    },
    [getUpdatedFile]
  );

  const handleShareConfirm = useCallback(
    async (usersToShare: SharedUser[], usersToUnshare: SharedUser[], selectedGroups: any, groupsUnshare: any, isGroupShare: boolean, permissions: { [key: string]: string }) => {
      if (!fileToShare) return;


      const operations: Promise<any>[] = [];

      // Queue unshare request if needed
      if (usersToUnshare.length > 0 && !isGroupShare) {
        operations.push(
          unshareDocumentFile({
            fileId: fileToShare.id,
            users: usersToUnshare.map((user) => ({
              id: user.id,
              type: "USER" as ShareholderType,
            })),
          },
            {
              onSuccess: () => {
                toast({
                  variant: "success",
                  title: t("SUCCESS"),
                  description: t("FILE_UNSHARED_SUCCESSFULLY"),
                });
              },
              onError: () => {
                toast({
                  variant: "destructive",
                  title: t("ERROR"),
                  description: t("FILE_UNSHARED_FAILED"),
                });
              },
            }
          )
        );
      }

      // Queue share request if needed
      if (usersToShare.length > 0 && !isGroupShare) {
        operations.push(
          shareDocumentFile({
            fileId: fileToShare.id,
            userIds: usersToShare.map((user) => ({
              id: user.id,
              type: "USER" as ShareholderType,
            })),
          },
            {
              onSuccess: () => {
                toast({
                  variant: "success",
                  title: t("SUCCESS"),
                  description: t("FILE_SHARED_SUCCESSFULLY"),
                });
              },
              onError: () => {
                toast({
                  variant: "destructive",
                  title: t("ERROR"),
                  description: t("FILE_SHARED_FAILED"),
                });
              },
            }
          )
        );
      }


      if (selectedGroups.length > 0 && isGroupShare) {
        operations.push(
          shareDocumentFile({
            fileId: fileToShare.id,
            userIds: selectedGroups.map((group: any) => ({
              id: group.id,
              type: "USER_GROUP" as ShareholderType,
            })),
          },
            {
              onSuccess: () => {
                toast({
                  variant: "success",
                  title: t("SUCCESS"),
                  description: t("FILE_SHARED_SUCCESSFULLY"),
                });
              },
              onError: () => {
                toast({
                  variant: "destructive",
                  title: t("ERROR"),
                  description: t("FILE_SHARED_FAILED"),
                });
              },
            }
          )
        );
      }

      if (groupsUnshare.length > 0 && isGroupShare) {
        operations.push(
          unshareDocumentFile({
            fileId: fileToShare.id,
            users: groupsUnshare.map((group: any) => ({
              id: group.id,
              type: "USER_GROUP" as ShareholderType,
            })),
          },
            {
              onSuccess: () => {
                toast({
                  variant: "success",
                  title: t("SUCCESS"),
                  description: t("FILE_UNSHARED_SUCCESSFULLY"),
                });
              },
              onError: () => {
                toast({
                  variant: "destructive",
                  title: t("ERROR"),
                  description: t("FILE_UNSHARED_FAILED"),
                });
              },
            }
          )
        );
      }

      try {
        // Run both operations in parallel
        await Promise.all(operations);
        // Invalidate once after all operations
        queryClient.invalidateQueries({ queryKey: ["document-files"] });
      } catch (error: any) {
        const errorDetail = error.message
          ? JSON.parse(error.message).detail
          : "Unknown error";

        if (errorDetail === "Failed to share file: File already shared with given user") {
          toast({
            variant: "destructive",
            title: t("ERROR"),
            description: t("FILE_ALREADY_SHARED"),
          });
        } else {
          toast({
            variant: "destructive",
            title: t("ERROR"),
            description: t("FILE_SHARED_FAILED"),
          });
        }
      } finally {
        // Close modal in all cases
        setIsShareModalOpen(false);
        setFileToShare(null);
      }
    },
    [fileToShare, newFiles, newFolders, selectedFileForDetails]
  );

  return {
    // State
    viewMode,
    searchQuery,
    newFiles,
    newFolders,
    renamedFiles,
    fileSharedUsers,
    filePermissions,

    // Modal states
    isSharedWithTenantModalOpen,
    isShareModalOpen,
    fileToShare,
    isRenameModalOpen,
    fileToRename,
    isDetailsOpen,
    selectedFileForDetails,

    // Handlers
    handleFileUpload,
    handleFolderCreate,
    handleDownload,
    handleDelete,
    handleShare,
    handleShareWithTenant,
    handleSharedWithTenantModalClose,
    handleSharedWithTenantConfirm,
    handleShareModalClose,
    handleRename,
    handleRenameConfirm,
    handleRenameModalClose,
    handleRenameUpdate,
    handleViewModeChange,
    handleSearchChange,
    handleCreateFile,
    handleViewDetails,
    handleShareConfirm,
    getUpdatedFile,
  };
};
